<template lang="pug">
.show-card
  .header(v-if="uiLoaded")
    .header-title.form-card-title
      inline-svg.header-title-icon.form-card-title-icon(
        :src="require('../../../../assets/icons/form/header/show_header.svg')"
      )
      span {{ headerTitle }}
      inline-svg.header-title-close-icon.form-card-close-icon(
        :src="require('../../../../assets/icons/form/header/close.svg')",
        @click="closeShow()"
      )
  .components
    tabs-component(
      v-if="components.tabs?.length > 1",
      :tabs="components.tabs",
      :rowId="rowId",
      @open-print-act="openPrintAct",
      @reload-show-ui="reloadShowUi",
      @open-edit="openEdit",
      @copy-row-item="copyRowItem",
      @open-issue-form="openIssueForm",
      @show-checklist="openChecklistPreview"
    )
    .info(v-else-if="components.tabs?.length === 1")
      section(v-for="section in components.tabs?.[0].sections", :key="section.id")
        component(
          :is="sectionComponents[section.type]",
          :data="section",
          :rowId="rowId",
          @open-print-act="openPrintAct",
          @reload-show-ui="reloadShowUi",
          @open-edit="openEdit",
          @open-issue-form="openIssueForm",
          @destroy-table-item="destroyTableItem",
          @copy-row-item="copyRowItem",
          @show-checklist="openChecklistPreview"
        )
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import backend from "@/api";
import tabsComponent from "./tabs.vue";
import sectionComponents from "./sections/index.js";
import { handleError } from "@/services/handleErrors";
import { useShowEdit } from "@/services/useShowEdit";

const props = defineProps({
  /** id текущего выбранного ряда. Нужен для запроса на получение данных show компонента. Пример: 1 */
  rowId: { type: null || Number, default: null },
  /** Название текущей страницы. Нужно для получения пути запроса данных show компонента. Пример: "acts" */
  name: { type: String, default: "" },
});

const emit = defineEmits([
  /**
   * Используется для закрытии формы. Не приинимает параметров */
  "close-show",
  /**
   * Используется для изменения loading значения main компонента. Принимает в качестве параметров boolean значение
   */
  "set-loading",
  /** Используется для открытия edit формы компонента. Принимает в качетсве параметра id текущего ряда */
  "open-edit",
  /** Используется для печати акта. Принимает дату для печати акта */
  "open-print-act",
  /** Эмит для открытия формы создания заявки из обращения */
  "open-issue-form",
  /** Эмит удаления элемента таблицы. В качестве параметра принимает id элемента. Пример: 1 */
  "destroy-table-item",
  /** Эмит копирования элемента. В качестве параметра принимает id исходного элемента. Пример: 1 */
  "copy-row-item",
  /** Эмит открытия предпросмотра чеклиста. В качестве параметра принимает action с data_url. */
  "show-checklist",
]);

const tabs = ref([]);
const uiLoaded = ref(false);
const headerTitle = ref("");
const components = ref({});

const rowId = computed(() => props.rowId);
const uiPath = computed(() => `/api/v3/ui/${props.name}/${rowId.value}`);

const getShowUi = async () => {
  try {
    const { data } = await backend.index(uiPath.value, {});

    headerTitle.value = data.title;
    components.value = data.components;
  } catch (err) {
    await handleError(err);
  } finally {
    uiLoaded.value = true;
  }
};

const reloadShowUi = async () => {
  uiLoaded.value = false;
  components.value = {};

  await getShowUi();
};

useShowEdit({
  path: () => `/api/v3/${props.name}`,
  rowId,
  reload: reloadShowUi,
});

const openPrintAct = data => {
  emit("open-print-act", data);
};

const closeShow = () => {
  emit("close-show");
};

const openEdit = id => {
  emit("open-edit", id);
};

const openIssueForm = id => {
  emit("open-issue-form", id);
};

const destroyTableItem = id => {
  emit("destroy-table-item", id);
};

const copyRowItem = id => {
  emit("copy-row-item", id);
};

const openChecklistPreview = handler => {
  emit("show-checklist", { handler, row: { id: rowId.value } });
};

onMounted(async () => {
  await getShowUi();
});
</script>

<style lang="scss" scoped>
.show-card {
  max-width: 650px;
  min-width: 650px;
  min-height: 100vh;
  position: absolute;
  top: 0;
  right: 0;
  padding: 16px;
  border-radius: 15px 0 0 15px !important;
  background: var(--show-card-background);
}
</style>
